import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Search,
  Refresh,
  Clear,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@mui/icons-material";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { GridColDef } from "@mui/x-data-grid";
import { DataGridPro, GridSortModel, GridFilterModel, GridLinkOperator } from '@mui/x-data-grid-pro';
import { BaseStateInformationPortalUrl } from "../Constants";
import { apiClient } from "../helper/api"
import useAuth, { authData } from "../context/AuthProvider";
import moment from "moment";
import formatPrice from "../helper/currencyFormatter";
import { AddDocument } from "./AddDocuments";
import useAppDialog from "../hooks/useAppDialog";
import { DialogResponse, DialogResult } from "../components/Dialog/dialogTypes";
import Swal from "sweetalert2";
import { safeMoment, formatUtcToLocal, writeXlsxFile } from "../helper/utils";
import { UploadedDocumentType } from "../models/TaxTransactionReportModel";
import { RiExpandUpDownLine } from "react-icons/ri";
import { Link } from "react-router-dom";


interface Transaction {
  rowNum: number;
  invoiceNumber: string;
  insuredName: string;
  policyNumber: string;
  homeState: string;
  transactionTypeName: string;
  invoiceDate: string;
  effectiveDate: string;
  policyExpDate: string;
  grossPremium: string;
  customerPolicyId: number;
  state: string;
  hasMessage: boolean;
  client: string;
  dueDate: string;
  removalRequestedAt?: string | null;
  isPrePortalHistorical?: boolean | number;
  /** Server-only sort key for Sent to MISC queue; safe to ignore in UI */
  sentQueueSortAt?: string;
}

interface Partner {
  partnersId: number;
  customerName: string;
}

export const CustomerTransactions: React.FC<{ currentUser: authData | undefined }> = ({ currentUser }) => {
  // Sample data - replace with your actual data source
  const [loading, setloading] = useState(false)
  const { user } = useAuth()
  const [itemList, setItemList] = useState<Transaction[]>([])
  const [ pageSize, setPageSize] = useState(50)
  const [ pageNumber, setPageNumber] = useState(1)
  const [ totalCount, setTotalCount] = useState(0)
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [], linkOperator: GridLinkOperator.And });
  const [ID, setID] = useState();
  const [openDoc, setOpenDoc] = useState(false);
  const [viewOnlyDoc, setViewOnlyDoc] = useState(false);
  const [selectedHomeState, setSelectedHomeState] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [insuredName, setInsuredName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [selectedPartner, setSelectedPartner] = useState('');
  const [clients, setClients] = useState<string[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedTab, setSelectedTab] = useState<'Outstanding' | 'SentToMISC'>('Outstanding');
  const [summaryData, setSummaryData] = useState({
    pastDue: 0,
    dueToday: 0,
    dueInNext10Days: 0,
    totalOutstanding: 0,
  });
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleClickOpenActionOptions = (customerPolicyId: number) => {
    const buttonEl = document.getElementById(`document-upload-action-button-${customerPolicyId}`) as HTMLElement | null;
    setActionMenuAnchorEl((prev) => (prev?.id === `document-upload-action-button-${customerPolicyId}` ? null : buttonEl));
  };
  const handleCloseActionOptions = () => {
    setActionMenuAnchorEl(null);
  };

  const token = user?.token;
  useEffect(() => {
    fetchData();
    fetchStateData();
    getPartnersList();
  }, [pageNumber, pageSize, filterModel, sortModel, selectedTab])

  // Handler for tab change - resets data and shows loading
  const handleTabChange = (tab: 'Outstanding' | 'SentToMISC') => {
    if (tab !== selectedTab) {
      setItemList([]); // Clear previous data immediately
      setTotalCount(0);
      setloading(true); // Show loading state
      setPageNumber(1); // Reset to first page
      // Sent tab default order is newest-first on the server; clear client sort so that applies
      if (tab === "SentToMISC") {
        setSortModel([]);
      }
      setSelectedTab(tab); // This will trigger useEffect to fetch new data
      ClearFilter();
    }
  };

  const initialStatedata = { stateId: 0, stateName: "" };
  const [States, setStates] = useState([initialStatedata]);
  const { openDialog_Message } = useAppDialog()
  
  const fetchStateData = () => {
    apiClient("/States/getStates", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
          .then((responseJson) => {
      if (!responseJson) return;
      setStates(responseJson.data);
    });
  };

  // Get Partners List API
  const getPartnersList = () => {
    apiClient("/customer/getPartnersList", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return;
        
        if (responseJson.data && responseJson.data.length > 0) {
          setPartners(responseJson.data);
          console.log("Partners data:", responseJson.data);
        } else {
          setPartners([]);
          console.log("No partners data available");
        }
      })
      .catch((error) => {
        console.error("Error fetching partners:", error);
        setPartners([]);
      });
  };

  // Refresh Partners List
  const refreshPartnersList = () => {
    getPartnersList();
  };

  const fetchData = () => {
    setloading(true); // Show loading state
    
    const requestBody: any = {
      pageSize: pageSize,
      pageNumber: pageNumber,
      field: sortModel?.[0]?.field || '',
      sort: sortModel?.[0]?.sort || '',
      filterModel: filterModel,
      policyNumber: policyNumber,
      insuredName: insuredName,
      invoiceNumber: invoiceNumber,
      selectedHomeState: selectedHomeState,
      selectedPartner: selectedPartner,
      isSentToMisc: selectedTab === 'SentToMISC',
    };

    apiClient(`/customer/getTransactions`, {
      method: "POST",
      headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(requestBody),
    })
         .then(responseJson => {
       if (!responseJson) return

       if (responseJson.data && responseJson.data.getCustomerTransactions_Respdata.length > 0) {
         setItemList(responseJson.data.getCustomerTransactions_Respdata);
         setTotalCount(responseJson.data.totalCount);
         
                   // Use partners data instead of extracting from transactions
          if (partners.length > 0) {
            const partnerNames = partners.map(partner => partner.customerName);
            setClients(partnerNames.sort());
          } else {
            setClients([]);
          }
         
         setloading(false);
       } else {
         setItemList([]);
         setTotalCount(0);
         setloading(false);
       }

       // Update summary data from API response
       if (responseJson.data) {
         setSummaryData({
           pastDue: responseJson.data.pastDueCount || 0,
           dueToday: responseJson.data.dueTodayCount || 0,
           dueInNext10Days: responseJson.data.dueNext10DaysCount || 0,
           totalOutstanding: responseJson.data.outstandingTotalCount || 0,
         });
       }
     })
    .catch(error => {
      setItemList([]);
      setTotalCount(0);
      setloading(false);
    });
  };

  const handleClose = () => {
    handleCloseActionOptions();
    const wasViewOnly = viewOnlyDoc;
    setOpenDoc(false);
    setViewOnlyDoc(false);
    if (!wasViewOnly) {
      fetchData();
    }
  };
  const handleSendMessage = async (custPolId: number, status?: string, viewOnly?: boolean) => {
    try {
      setloading(true);

      openDialog_Message({
        custPolId: String(custPolId),
        viewOnly: viewOnly
      }).then((res: DialogResponse) => {
        if (res.dialogResult === DialogResult.OK) {
          // Message sent successfully
        }
        return res;
      });
      setloading(false);

    } catch (error) {
      setloading(false);
    }
  };

  const UploadDocument = (id: any, isViewOnly: boolean = false) => {
    setID(id);
    setViewOnlyDoc(isViewOnly);
    setOpenDoc(true);
  };

  /** Sent-to-MISC: Message center for threads with messages or pre-portal historical rows (info banner in dialog). */
  const rowHasMessageCenter = (row: Transaction) =>
    Boolean(row.hasMessage) ||
    row.isPrePortalHistorical === true ||
    row.isPrePortalHistorical === 1;

  const isClientAdmin = Boolean(
    user?.userRoles
      ?.split(",")
      .map((r) => r.trim())
      .includes("Client Admin")
  );

  const requestTransactionRemoval = async (customerPolicyId: number) => {
    const result = await Swal.fire({
      title: "Request removal",
      text: "Ask MISC to remove this transaction from filing?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#007BFF",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    const reasonResult = await Swal.fire({
      title: "Reason for removal",
      html:
        '<p style="text-align:left;margin:0 0 12px;font-size:15px;color:#555;">Please describe the reason for requesting to remove this transaction:</p>',
      input: "textarea",
      inputPlaceholder: "Enter your reason…",
      inputAttributes: {
        "aria-label": "Reason for removal",
      },
      showCancelButton: true,
      confirmButtonColor: "#007BFF",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Submit",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        const t = (value ?? "").trim();
        if (!t) {
          return "Please enter a reason for removal.";
        }
        return undefined;
      },
    });
    if (!reasonResult.isConfirmed || reasonResult.value === undefined) return;

    const removalReason = String(reasonResult.value).trim();
    setloading(true);
    try {
      const res = await apiClient(`/customer/requestTransactionRemoval`, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify({ customerPolicyId, removalReason }),
      });
      if (!res?.status) {
        Swal.fire({
          icon: "error",
          title: "Unable to request removal",
          text: res?.message || "Please try again.",
          confirmButtonText: "OK",
        });
        return;
      }
      Swal.fire({
        icon: "success",
        title: "Request sent",
        text: "MISC has been notified.",
        confirmButtonText: "OK",
      });
      fetchData();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error occurred.",
        confirmButtonText: "OK",
      });
    } finally {
      setloading(false);
    }
  };

  const SentToMisc = async (customerPolicyId: any) => {
    const [uploadedRes, revertedRes] = await Promise.all([
      apiClient(`/customer/getUploadedDocuments?customerPolicyId=${customerPolicyId}`, {
        method: "GET",
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        })
      }),
      apiClient(`/customer/getRevertedDocuments?customerPolicyId=${customerPolicyId}`, {
        method: "GET",
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        })
      })
    ]);
    
    if (!uploadedRes && !revertedRes) return;
    
    const uploadedDocuments = uploadedRes?.data ?? [];
    const revertedDocuments = revertedRes?.data ?? [];
    
    const hasUploadedDocs = uploadedDocuments.some((doc: UploadedDocumentType) => doc.documentTypeId !== 0);
    const hasRevertedDocs = revertedDocuments.some((doc: UploadedDocumentType) => doc.documentTypeId !== 0);
    
    if (!hasUploadedDocs && !hasRevertedDocs) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Please upload at least one document before sending to MISC',
        confirmButtonText: 'OK'
      });
      return;
    }

    Swal.fire({
      title: 'Send to MISC',
      text: 'Are you sure you want to send this transaction to MISC?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#007BFF',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      padding: '20px',
      backdrop: true,
      customClass: {
          popup: 'custom-swal-popup',
          confirmButton: 'btn-radius',
          cancelButton: 'btn-radius',
      },
      width: '500px',
  })
  .then(async (result: { isConfirmed: any; }) => {
    if (result.isConfirmed) {
      try {
        // If there are reverted documents, convert them to uploaded documents first
        if (hasRevertedDocs) {
          await apiClient(`/customer/convertRevertedToUploaded?customerPolicyId=${customerPolicyId}`, {
            method: "GET",
            headers: new Headers({
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            })
          });
        }       
        // Now send to MISC
        const res = await apiClient(`/customer/SentToMisc?customerPolicyId=${customerPolicyId}`, {
          method: "GET",
          headers: new Headers({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          })
        });
        
        if (!res) return;
        if (res.data != null) {
          Swal.fire({
            title: 'Success!',
            text: 'The transaction has been successfully sent to MISC.',
            icon: 'success',
            confirmButtonColor: '#007BFF',
            confirmButtonText: 'OK',
            padding: '20px',
            customClass: {
              popup: 'custom-swal-popup',
              confirmButton: 'btn-radius',
            },
            width: '400px',
          }).then((result) => {
            fetchData();
          });
        }
      } catch (error) {
        console.error('Error sending to MISC:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'An error occurred while sending to MISC. Please try again.',
          confirmButtonText: 'OK'
        });
      }
    }
  });
  }

  const handlePolicyNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setPolicyNumber(inputValue);
  };

  const handleInsuredNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setInsuredName(inputValue);
  };

  const handleInvoiceNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setInvoiceNumber(inputValue);
  };

  function RunSearch() {
    setloading(true);
    fetchData();
  }

     function ClearFilter() {
     setloading(true);
     setSelectedHomeState('');
     setPolicyNumber('');
     setInsuredName('');
     setInvoiceNumber('');
     setSelectedPartner('');
     setFilterModel({ items: [], linkOperator: GridLinkOperator.And });
}

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
    setPageNumber(1); // Reset to first page when filter changes
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    setPageNumber(1); // Reset to first page when sort changes
  };

      const handleExport = async () => {
       try {
         setloading(true); // Add loading state for export
         
        // Export all filtered data
      const requestBody: any = {
        pageSize: 10000, // Large number to get all data
        pageNumber: 1,
        field: sortModel?.[0]?.field || '',
        sort: sortModel?.[0]?.sort || '',
        filterModel: filterModel,
        policyNumber: policyNumber,
        insuredName: insuredName,
        invoiceNumber: invoiceNumber,
        selectedHomeState: selectedHomeState,
        selectedPartner: selectedPartner,
        isSentToMisc: selectedTab === 'SentToMISC',
      };

      const response = await apiClient(`/customer/getTransactions`, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(requestBody),
      });

      if (!response || !response.data || !response.data.getCustomerTransactions_Respdata) {
        Swal.fire({
          icon: 'error',
          title: 'Export Failed',
          text: 'No data available to export',
          confirmButtonText: 'OK'
        });
        return;
      }

      const exportData = response.data.getCustomerTransactions_Respdata;

      // Define headers for export - match UI display logic
       const headers = partners.length > 1 ? [
        'Filing Due Date',
         'Client',
         'Invoice Number',
         'Insured Name', 
         'Policy Number',
         'Home State',
         'Transaction Type',
         'Invoice Date',
         'Policy Effective Date',
         'Policy Expiration Date',
         'Gross Premium'
       ] : [
        'Filing Due Date',
        'Invoice Number',
        'Insured Name', 
        'Policy Number',
        'Home State',
        'Transaction Type',
        'Invoice Date',
        'Policy Effective Date',
        'Policy Expiration Date',
        'Gross Premium'
      ];

             // Prepare data for export - match UI display logic
       const exportRows = exportData.map((item: Transaction) => {
         if (partners.length > 1) {
           return [
             item.dueDate ? formatUtcToLocal(item.dueDate, "MM/DD/YYYY") : '',
             item.client || '',
             item.invoiceNumber || '',
             item.insuredName || '',
             item.policyNumber || '',
             item.homeState || '',
             item.transactionTypeName || '',
             item.invoiceDate ? formatUtcToLocal(item.invoiceDate, "MM/DD/YYYY") : '',
             item.effectiveDate ? formatUtcToLocal(item.effectiveDate, "MM/DD/YYYY") : '',
             item.policyExpDate ? formatUtcToLocal(item.policyExpDate, "MM/DD/YYYY") : '',
             item.grossPremium ? formatPrice(item.grossPremium.replace(/[,\$]/g, "").trim()) : ''
           ];
         } else {
          return [
            item.dueDate ? formatUtcToLocal(item.dueDate, "MM/DD/YYYY") : '',
            item.invoiceNumber || '',
            item.insuredName || '',
            item.policyNumber || '',
            item.homeState || '',
            item.transactionTypeName || '',
            item.invoiceDate ? formatUtcToLocal(item.invoiceDate, "MM/DD/YYYY") : '',
            item.effectiveDate ? formatUtcToLocal(item.effectiveDate, "MM/DD/YYYY") : '',
            item.policyExpDate ? formatUtcToLocal(item.policyExpDate, "MM/DD/YYYY") : '',
            item.grossPremium ? formatPrice(item.grossPremium.replace(/[,\$]/g, "").trim()) : ''
          ];
        }
      });

      // Combine headers and data
      const exportArray = [headers, ...exportRows];

      // Generate filename with timestamp
      const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
      const filename = `CustomerTransactions_${exportData.length}_${timestamp}.xlsx`;

            // Export to Excel
      await writeXlsxFile(exportArray, filename);

      // Show success popup
      Swal.fire({
        icon: 'success',
        title: 'Export Successful',
        text: `All data has been exported.`,
        confirmButtonText: 'OK'
      });

             } catch (error) {
          console.error('Export error:', error);
          Swal.fire({
            icon: 'error',
            title: 'Export Failed',
            text: 'An error occurred while exporting the data',
            confirmButtonText: 'OK'
          });
        } finally {
          setloading(false); // Always clear loading state
        }
  };



  const columns: GridColDef[] = [
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 90,
      sortable: false,
      filterable: false,
      resizable: false,
      disableColumnMenu: true,
      headerAlign: "left",
      align: "left",
      hideSortIcons: true,
      renderCell: (params) => (
        <>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
            <Button
              size="small"
              style={{ minWidth: "0px" }}
              id={"document-upload-action-button-" + params.row.customerPolicyId}
              aria-controls={
                actionMenuAnchorEl?.id === "document-upload-action-button-" + params.row.customerPolicyId
                  ? "document-upload-action-menu-" + params.row.customerPolicyId
                  : undefined
              }
              aria-haspopup="true"
              aria-expanded={
                actionMenuAnchorEl?.id === "document-upload-action-button-" + params.row.customerPolicyId
                  ? "true"
                  : undefined
              }
              onClick={(e) => {
                e.stopPropagation();
                handleClickOpenActionOptions(params.row.customerPolicyId);
              }}
            >
              <MoreVertOutlinedIcon fontSize="small" />
            </Button>
            <Menu
              id={"document-upload-action-menu-" + params.row.customerPolicyId}
              aria-labelledby={"document-upload-action-button-" + params.row.customerPolicyId}
              anchorEl={actionMenuAnchorEl}
              open={Boolean(
                actionMenuAnchorEl &&
                  actionMenuAnchorEl.id === "document-upload-action-button-" + params.row.customerPolicyId
              )}
              onClose={handleCloseActionOptions}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              MenuListProps={{
                "aria-labelledby": "document-upload-action-button-" + params.row.customerPolicyId,
              }}
              sx={{
                "& .MuiMenuItem-root": {
                  fontSize: "14px",
                  padding: "8px 16px",
                },
              }}
            >
              {selectedTab === "SentToMISC" ? (
                <>
                  <MenuItem
                    onClick={(e) => {
                      handleCloseActionOptions();
                      e.stopPropagation();
                      UploadDocument(params.row.customerPolicyId, true);
                    }}
                  >
                    <VisibilityOutlinedIcon fontSize="small" color="action" style={{ marginRight: 8 }} />
                    View Document
                  </MenuItem>
                  {params.row.hasMessage && (
                    <MenuItem
                      onClick={(e) => {
                        handleCloseActionOptions();
                        e.stopPropagation();
                        handleSendMessage(params.row.customerPolicyId, params.row.status, true);
                      }}
                    >
                      <MailOutlineIcon fontSize="small" color="action" style={{ marginRight: 8 }} />
                      View Messages
                    </MenuItem>
                  )}
                </>
              ) : (
                <>
                  <MenuItem
                    onClick={(e) => {
                      handleCloseActionOptions();
                      e.stopPropagation();
                      SentToMisc(params.row.customerPolicyId);
                    }}
                  >
                    <SendOutlinedIcon fontSize="small" color="action" style={{ marginRight: 8 }} />
                    Send to MISC
                  </MenuItem>
                  <MenuItem
                    onClick={(e) => {
                      handleCloseActionOptions();
                      e.stopPropagation();
                      UploadDocument(params.row.customerPolicyId);
                    }}
                  >
                    <FileUploadOutlinedIcon fontSize="small" color="action" style={{ marginRight: 8 }} />
                    Upload Document
                  </MenuItem>
                  {user?.isSIPAccess && (
                    <MenuItem
                      component="a"
                      href={`${BaseStateInformationPortalUrl}/${params.row.state}.aspx#PolicyDocuments`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        handleCloseActionOptions();
                        e.stopPropagation();
                      }}
                    >
                      <DescriptionOutlinedIcon fontSize="small" color="action" style={{ marginRight: 8 }} />
                      SIP
                    </MenuItem>
                  )}
                  {isClientAdmin && !params.row.removalRequestedAt && (
                    <MenuItem
                      onClick={(e) => {
                        handleCloseActionOptions();
                        e.stopPropagation();
                        requestTransactionRemoval(params.row.customerPolicyId);
                      }}
                    >
                      <HighlightOffOutlinedIcon fontSize="small" color="action" style={{ marginRight: 8 }} />
                      Remove
                    </MenuItem>
                  )}
                </>
              )}
            </Menu>
            {params.row.hasMessage && selectedTab !== "SentToMISC" ? (
                    <IconButton
                      size="small"
                      sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
                      onClick={(e) => {
                        handleCloseActionOptions();
                        e.stopPropagation();
                        handleSendMessage(params.row.customerPolicyId, params.row.status);
                      }}
                    >
                      <Tooltip placement="top" arrow title="Send Message">
                      <MailOutlineIcon fontSize="small" color="action" />
                      </Tooltip>
                    </IconButton>
                  ) : selectedTab === "SentToMISC" && rowHasMessageCenter(params.row) ? (
                    <IconButton
                      size="small"
                      sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
                      onClick={(e) => {
                        handleCloseActionOptions();
                        e.stopPropagation();
                        handleSendMessage(params.row.customerPolicyId, params.row.status, true);
                      }}
                    >
                      <Tooltip placement="top" arrow title="View Messages">
                        <MailOutlineIcon fontSize="small" color="action" />
                      </Tooltip>
                    </IconButton>
                  ) : null}
          </Box>
        </>
      ),
    },
    {
      field: 'customerPolicyId', 
      hide: true,
    },
    ...(selectedTab === 'Outstanding' ? [{
      field: 'dueDate',
      headerName: 'Filing Due Date',
      type: 'date',
      flex: 1,
      minWidth: 190,
      hideSortIcons: true,
      valueGetter: (params) => {
        if (!params.value) return null;
        return new Date(params.value);
      },
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'dueDate'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )
      },
      renderCell: (params) => {
        const dueDate = params.row.dueDate;
        const isPastDue = dueDate && safeMoment(dueDate).isBefore(moment(), 'day');
        return (
          <div style={{ color: isPastDue ? '#d32f2f' : 'inherit'}}>
            {(dueDate != null && dueDate != '') ? formatUtcToLocal(dueDate, "MM/DD/YYYY") : ""}
          </div>
        );
      }
    } as GridColDef] : []),
         ...(partners.length > 1 ? [{ 
       field: 'client', 
       headerName: 'Client',
       flex: 1,
       minWidth: 200,
       hideSortIcons: true,
       renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'client'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
          return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
          )
        },
     } as GridColDef] : []),
    { 
      field: 'invoiceNumber', 
      headerName: 'Invoice Number',
      flex: 1,
      minWidth: 180,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'invoiceNumber'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
          return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
          )
      },
    },
    { 
      field: 'insuredName', 
      headerName: 'Insured Name',
      flex: 1,
      minWidth: 165,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'insuredName'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
          return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
          )
      },
    },
    { 
      field: 'policyNumber', 
      headerName: 'Policy Number',
      flex: 1,
      minWidth: 160,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'policyNumber'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
          return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
          )
      },
    },
    { 
      field: 'homeState', 
      headerName: 'Home State',
      flex: 1,
      minWidth: 160,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'homeState'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
          return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
          )
      },
    },
    { 
      field: 'transactionTypeName', 
      headerName: 'Transaction Type',
      flex: 1,
      minWidth: 185,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'transactionTypeName'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
          return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
          )
      },
    },
    { 
      field: 'invoiceDate', 
      headerName: 'Inv Date',
      type: 'date',
      flex: 1,
      minWidth: 120,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'invoiceDate'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
          return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
          )
      },
      valueGetter: (params) => {
        if (!params.value) return null;
        return new Date(params.value);
      },
      renderCell: (params) =>
      (
        <div>
          {(params.row.invoiceDate != null && params.row.invoiceDate != '') ? formatUtcToLocal(params.row.invoiceDate, "MM/DD/YYYY") : ""}
        </div>
      )
    },
    { 
      field: 'effectiveDate', 
      headerName: 'Policy Eff Date',
      type: 'date',
      flex: 1,
      minWidth: 165,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'effectiveDate'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
          return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
          )
      },
      valueGetter: (params) => {
        if (!params.value) return null;
        return new Date(params.value);
      },
      renderCell: (params) =>
      (
        <div>
          {(params.row.effectiveDate != null && params.row.effectiveDate != '') ? formatUtcToLocal(params.row.effectiveDate, "MM/DD/YYYY") : ""}
        </div>
      )
    },
    { 
      field: 'policyExpDate', 
      headerName: 'Policy Exp Date',
      type: 'date',
      flex: 1,
      minWidth: 175,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'policyExpDate'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
          return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
          )
      },
      valueGetter: (params) => {
        if (!params.value) return null;
        return new Date(params.value);
      },
      renderCell: (params) =>
      (
        <div>
          {(params.row.policyExpDate != null && params.row.policyExpDate != '') ? formatUtcToLocal(params.row.policyExpDate, "MM/DD/YYYY") : ""}
        </div>
      )
    },
    {
      field: 'grossPremium',
      headerName: 'Gross Premium',
      flex: 1,
      minWidth: 165,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'grossPremium'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
          return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
          )
      },
      renderCell: (params) => {
        let grossPremium = params.value || '';
        let cleanedValue = grossPremium.replace(/[,\$]/g, "").trim();

        if (cleanedValue.startsWith("(") && cleanedValue.endsWith(")")) {
          cleanedValue = "-" + cleanedValue.slice(1, -1);
        }

        const numericValue = parseFloat(cleanedValue);

        return isNaN(numericValue) ? grossPremium : `${formatPrice(numericValue.toString())}`;
      }
    }
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <style>
        {`
          input::placeholder {
            color: #333;
            opacity: 1;
          }
        `}
      </style>

      {/* Top bar: Tabs | Filters | Refresh | Export (match Tabs.tsx) */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#fafbfc",
          minHeight: 56,
          mb: 1,
          marginLeft: { xs: "-24px", md: "0px" },
          marginRight: { xs: "-24px", md: "0px" },
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={(_, newValue: "Outstanding" | "SentToMISC") => handleTabChange(newValue)}
          sx={{
            minHeight: 48,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9375rem" },
            "& .Mui-selected": { color: "#2099d2" },
            "& .MuiTabs-indicator": {
              backgroundColor: "#2099d2",
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab value="Outstanding" label="Outstanding" />
          <Tab value="SentToMISC" label="Sent to MISC" />
        </Tabs>
        <Box sx={{ flex: 1, minWidth: 120 }} />
        <Button
          variant="outlined"
          size="medium"
          onClick={() => window.location.reload()}
          startIcon={<Refresh />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#e2e8f0",
            color: "#475569",
            "&:hover": {
              borderColor: "#2099d2",
              backgroundColor: "#2099d214",
              color: "#2099d2",
            },
          }}
        >
          Refresh
        </Button>
        <Button
          variant="outlined"
          size="medium"
          onClick={handleExport}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#e2e8f0",
            color: "#475569",
            "&:hover": {
              borderColor: "#2099d2",
              backgroundColor: "#2099d214",
              color: "#2099d2",
            },
          }}
        >
          Export
        </Button>
      </Box>

      <Box
        sx={{
          width: "100%",
          py: 2,
          px: 2,
          // backgroundColor: "#ffffff",
          // borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Grid container spacing={2} className="filter-grid" display="flex">
            <Grid item xl={2} lg={4} sm={6} xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  id="home-state"
                  sx={{
                    marginTop: '-4px !important',
                    '& .MuiAutocomplete-input': {
                      padding: '0px 4px 0px 5px !important',
                    }
                  }}
                  options={States}
                  getOptionLabel={(option) => option.stateName || ""}
                  onChange={(_event, value) => {
                    setSelectedHomeState(value ? String(value.stateId) : "");
                  }}
                  value={
                    selectedHomeState === ""
                      ? null
                      : States.find((item) => String(item.stateId) === String(selectedHomeState)) ?? null
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Home State"
                      sx={{
                        '.MuiFormLabel-root.Mui-focused': {
                          color: (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0) ? '#000' : '#cccccc !important'
                        },
                        '& .MuiInputBase-root': { marginTop: '5px !important' },
                        '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                        '& .MuiFormLabel-root': { transform: 'translate(14px, 13px) scale(1)' },
                        '& .MuiInputLabel-shrink': { transform: 'translate(14px, -2px) scale(0.75)' },
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink:
                          (selectedHomeState !== "" &&
                            selectedHomeState !== "0") ||
                          (typeof params.inputProps.value === "string" && params.inputProps.value.length > 0)
                            ? true
                            : false,
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xl={2} lg={4} sm={6} xs={12}>
              <TextField
                name="PolicyNumber"
                size="small"
                value={policyNumber}
                label="Policy Number"
                onChange={handlePolicyNumberChange}
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                  "& .MuiInputBase-root": { marginTop: "1px !important" },
                  "& .MuiInputBase-input": { padding: "4.5px 14px !important" },
                }}
              />
            </Grid>
            <Grid item xl={2} lg={4} sm={6} xs={12}>
              <TextField
                name="InsuredName"
                size="small"
                value={insuredName}
                label="Insured Name"
                onChange={handleInsuredNameChange}
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                  "& .MuiInputBase-root": { marginTop: "1px !important" },
                  "& .MuiInputBase-input": { padding: "4.5px 14px !important" },
                }}
              />
            </Grid>
            <Grid item xl={2} lg={4} sm={6} xs={12}>
              <TextField
                name="InvoiceNumber"
                size="small"
                value={invoiceNumber}
                label="Invoice Number"
                onChange={handleInvoiceNumberChange}
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                  "& .MuiInputBase-root": { marginTop: "1px !important" },
                  "& .MuiInputBase-input": { padding: "4.5px 14px !important" },
                }}
              />
            </Grid>
            {partners.length > 1 && (
              <Grid item xl={2} lg={4} sm={6} xs={12}>
                <FormControl size="small" fullWidth>
                  <Autocomplete
                    id="select-client"
                    sx={{
                      marginTop: "-4px !important",
                      "& .MuiAutocomplete-input": {
                        padding: "0px 4px 0px 5px !important",
                      },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                    }}
                    options={partners}
                    getOptionLabel={(option) => option.customerName || ""}
                    onChange={(_event, value) => {
                      setSelectedPartner(value ? String(value.partnersId) : "");
                    }}
                    value={
                      selectedPartner === ""
                        ? null
                        : partners.find((item) => String(item.partnersId) === String(selectedPartner)) ?? null
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Client"
                        sx={{
                          ".MuiFormLabel-root.Mui-focused": {
                            color:
                              typeof params.inputProps.value === "string" && params.inputProps.value.length > 0
                                ? "#000"
                                : "#cccccc !important",
                          },
                          "& .MuiInputBase-root": { marginTop: "5px !important" },
                          "& .MuiOutlinedInput-root": { padding: "4.5px 9px" },
                          "& .MuiFormLabel-root": { transform: "translate(14px, 13px) scale(1)" },
                          "& .MuiInputLabel-shrink": { transform: "translate(14px, -2px) scale(0.75)" },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                        }}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          shrink:
                            selectedPartner !== "" ||
                            (typeof params.inputProps.value === "string" && params.inputProps.value.length > 0)
                              ? true
                              : false,
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm="auto">
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={() => RunSearch()}
                  startIcon={<Search />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: "#2099d2",
                    "&:hover": { backgroundColor: "#176990" },
                  }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() => ClearFilter()}
                  startIcon={<Clear />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: "#e2e8f0",
                    color: "#475569",
                    "&:hover": { borderColor: "#2099d2", color: "#2099d2" },
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
        </Grid>
      </Box>

      {/* Welcome + summary card (match AdminHomePage / Outstanding style) */}
      <Box
        sx={{
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          mb: 2,
          px: 3,
          py: 2.5,
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
          border: "1px solid #e2e8f0",
        }}
      >
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 600, color: "#334155", mb: 0.25 }}>
          Hello, {currentUser?.userFirstName} {currentUser?.userlastName}!
        </Typography>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 400, color: "#64748b", mb: selectedTab === "Outstanding" ? 2 : 0 }}>
          Upload filing documents using the <strong>Document Upload</strong> button for each transaction.
        </Typography>
        {selectedTab === "Outstanding" && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.5 }}>
            <Box sx={{ px: 2, py: 1, borderRadius: "8px", backgroundColor: "#fef2f2", color: "#b91c1c", fontWeight: 600, fontSize: "0.875rem" }}>
              Past Due: {summaryData.pastDue}
            </Box>
            <Box sx={{ px: 2, py: 1, borderRadius: "8px", backgroundColor: "#f8fafc", color: "#334155", fontWeight: 600, fontSize: "0.875rem" }}>
              Due Today: {summaryData.dueToday}
            </Box>
            <Box sx={{ px: 2, py: 1, borderRadius: "8px", backgroundColor: "#f8fafc", color: "#334155", fontWeight: 600, fontSize: "0.875rem" }}>
              Due in Next 10 Days: {summaryData.dueInNext10Days}
            </Box>
            <Box sx={{ px: 2, py: 1, borderRadius: "8px", backgroundColor: "#f0f9ff", color: "#2099d2", fontWeight: 600, fontSize: "0.875rem" }}>
              Total Outstanding: {summaryData.totalOutstanding}
            </Box>
          </Box>
        )}
      </Box>

      {/* Grid wrapper (match OutstandingFilings) */}
      <Box
        sx={{
          height: { xs: "calc(100vh - 320px)", sm: "calc(100vh - 320px)" },
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)",
          border: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          marginBottom: 4,
          position: "relative",
        }}
      >
        <Box sx={{ flex: "1 1 0%", overflow: "auto", minHeight: 0 }}>
          <DataGridPro
            getRowId={(r) => r.rowNum}
            rows={itemList}
            columns={columns}
            rowCount={totalCount}
            pageSize={pageSize}
            loading={loading}
            pagination
            paginationMode="server"
            onPageChange={(newPage: number) => setPageNumber(newPage + 1)}
            rowsPerPageOptions={[10, 25, 50]}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            sortingMode="server"
            sortingOrder={["asc", "desc"]}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            disableSelectionOnClick
            sx={{
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              border: "none",
              fontSize: "14px",
              color: "#475569",
              backgroundColor: "#ffffff",
              /* Loading overlay sits before the virtual scroller in DOM; raise it above striped rows */
              "& .MuiDataGrid-virtualScroller": { zIndex: 0, border: "none" },
              "& .MuiDataGrid-overlay": { zIndex: 1 },
              ...(loading
                ? {
                    "& .MuiDataGrid-main > div:first-of-type": {
                      zIndex: 10,
                    },
                  }
                : {}),
              "& .MuiDataGridPro-columnHeaderTitle": {
                whiteSpace: "normal",
                lineHeight: 1.35,
                fontWeight: 600,
                fontSize: "12px",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "#64748b",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f8fafc",
                borderBottom: "2px solid #e2e8f0",
                borderTop: "2px solid #e2e8f0",
                minHeight: "52px !important",
                maxHeight: "52px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                "&:focus, &:focus-within": { outline: "none" },
              },
              "& .MuiDataGrid-row": {
                transition: "background-color 0.15s ease",
                "&:nth-of-type(even)": {
                  backgroundColor: "#fafbfc",
                },
                "&:hover": {
                  backgroundColor: "#f1f5f9 !important",
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f1f5f9",
                fontSize: "14px",
                color: "#334155",
                "&:focus": { outline: "none" },
              },
              "& .MuiDataGrid-main": { border: "none", position: "relative" },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e2e8f0",
                backgroundColor: "#f8fafc",
                minHeight: 56,
              },
              "& .MuiTablePagination-root": { color: "#64748b", fontSize: "13px" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { marginBottom: 0 },
              "& .MuiTablePagination-select": {
                fontSize: "13px",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
              },
              "& .MuiIconButton-root": {
                color: "#64748b",
                "&:hover": { backgroundColor: "#f1f5f9", color: "#475569" },
              },
            }}
          />
        </Box>
      </Box>
      <AddDocument open={openDoc} handleClose={handleClose} customerPolicyId={ID} currentUser={currentUser} viewOnly={viewOnlyDoc}></AddDocument>
    </Box>

  );
};